import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getOrganizationMembers, getOrganizationInvitations, createInvitation } from '../../lib/api'
import type { OrgMember, Invitation } from '../../lib/api'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { getErrorMessage } from '../../lib/errors'

const ROLE_OPTIONS = [
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Administrador' },
]

function formatExpiry(expiresAt: string): string {
  const date = new Date(expiresAt)
  return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })
}

function isExpired(expiresAt: string): boolean {
  return new Date(expiresAt) < new Date()
}

function RoleBadge({ role }: { role: string }) {
  const isAdmin = role === 'admin'
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        isAdmin
          ? 'bg-indigo-100 text-indigo-700'
          : 'bg-slate-100 text-slate-600'
      }`}
    >
      {isAdmin ? 'Administrador' : 'Editor'}
    </span>
  )
}

function MembersTable({ members }: { members: OrgMember[] }) {
  if (members.length === 0) {
    return (
      <p className="text-sm text-slate-500 py-4 text-center">No hay miembros en la organización.</p>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-2 pr-4 font-medium text-slate-600">Nombre</th>
            <th className="text-left py-2 pr-4 font-medium text-slate-600">Correo</th>
            <th className="text-left py-2 font-medium text-slate-600">Rol</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <tr key={member.id} className="border-b border-slate-100 last:border-0">
              <td className="py-3 pr-4 text-slate-800">
                {member.full_name ?? <span className="text-slate-400 italic">Sin nombre</span>}
              </td>
              <td className="py-3 pr-4 text-slate-600">{member.email ?? '—'}</td>
              <td className="py-3">
                <RoleBadge role={member.role} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function InvitationsTable({ invitations }: { invitations: Invitation[] }) {
  const pending = invitations.filter((inv) => !inv.accepted_at)

  if (pending.length === 0) {
    return (
      <p className="text-sm text-slate-500 py-4 text-center">No hay invitaciones pendientes.</p>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-2 pr-4 font-medium text-slate-600">Correo</th>
            <th className="text-left py-2 pr-4 font-medium text-slate-600">Rol</th>
            <th className="text-left py-2 font-medium text-slate-600">Vence</th>
          </tr>
        </thead>
        <tbody>
          {pending.map((inv) => {
            const expired = isExpired(inv.expires_at)
            return (
              <tr key={inv.id} className="border-b border-slate-100 last:border-0">
                <td className="py-3 pr-4 text-slate-800">{inv.email}</td>
                <td className="py-3 pr-4">
                  <RoleBadge role={inv.role} />
                </td>
                <td className="py-3">
                  <span className={expired ? 'text-red-500' : 'text-slate-500'}>
                    {formatExpiry(inv.expires_at)}
                    {expired && ' (expirada)'}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function SettingsTeamSection() {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('editor')

  const {
    data: members = [],
    isLoading: loadingMembers,
  } = useQuery<OrgMember[]>({
    queryKey: ['org-members'],
    queryFn: getOrganizationMembers,
  })

  const {
    data: invitations = [],
    isLoading: loadingInvitations,
  } = useQuery<Invitation[]>({
    queryKey: ['org-invitations'],
    queryFn: getOrganizationInvitations,
  })

  const inviteMutation = useMutation({
    mutationFn: () => createInvitation({ email: email.trim(), role }),
    onSuccess: () => {
      toast.success(`Invitación enviada a ${email}`)
      setEmail('')
      setRole('editor')
      queryClient.invalidateQueries({ queryKey: ['org-invitations'] })
    },
    onError: (err) => {
      toast.error('Error al enviar invitación', { description: getErrorMessage(err) })
    },
  })

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    inviteMutation.mutate()
  }

  const busy = inviteMutation.isPending

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-slate-800">Equipo</h2>
      <p className="text-sm text-slate-500 mt-1">
        Gestiona los miembros de tu organización e invita a nuevos colaboradores.
      </p>

      {/* Members table */}
      <div className="mt-6 border border-slate-200 rounded-lg p-4 bg-slate-50/50">
        <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">
          Miembros actuales
        </h3>
        {loadingMembers ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner size="sm" />
          </div>
        ) : (
          <MembersTable members={members} />
        )}
      </div>

      {/* Invite form */}
      <div className="mt-4 border border-slate-200 rounded-lg p-4 bg-slate-50/50">
        <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">
          Invitar colaborador
        </h3>
        <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            className="input flex-1"
            placeholder="correo@ejemplo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            required
          />
          <select
            className="input w-full sm:w-40"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={busy}
          >
            {ROLE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="btn btn-primary whitespace-nowrap"
            disabled={busy || !email.trim()}
          >
            {busy ? 'Enviando...' : 'Enviar invitación'}
          </button>
        </form>
      </div>

      {/* Pending invitations table */}
      <div className="mt-4 border border-slate-200 rounded-lg p-4 bg-slate-50/50">
        <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">
          Invitaciones pendientes
        </h3>
        {loadingInvitations ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner size="sm" />
          </div>
        ) : (
          <InvitationsTable invitations={invitations} />
        )}
      </div>
    </section>
  )
}
